import React, { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import { StringParam } from 'serialize-query-params';
import { useQueryParams } from 'use-query-params';

import { type FolderDTOStatus } from '@/api/generated';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import { useFolderStatusActions } from '@/hooks/useFolderStatusActions';
import typography from '@/theme/typography';

const URL_CONFIG_MAP = {
  locationSlug: StringParam
};

const FolderStatusSelect: FC = () => {
  const { watch, setValue } = useFormContext();

  const { organizationSlug } = useParams() as { organizationSlug: string };
  const [{ locationSlug }] = useQueryParams(URL_CONFIG_MAP);

  const currentStatus = watch('status');

  const setStatus = useCallback(
    (newStatus: FolderDTOStatus) => {
      setValue('status', newStatus);
    },
    [setValue]
  );

  const {
    change,
    isLoading: isStatusLoading,
    isFinalState,
    availableStatuses
  } = useFolderStatusActions({
    currentStatus,
    onChange: setStatus,
    organizationSlug,
    folderSlug: locationSlug!
  });

  const handleChange = useCallback(
    (newStatus: string) => {
      change(newStatus as FolderDTOStatus);
    },
    [change]
  );

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <GeneralStatusDropdownSelect
        name="status"
        onChange={handleChange}
        value={currentStatus}
        activeOptions={availableStatuses as string[]}
        isDisabled={isFinalState}
        sx={{
          mx: 2,
          '& .DropdownSelect-control': {
            minWidth: 122,
            minHeight: 'unset',
            '& .DropdownSelect-optionIcon .CircleIcon-root': {
              m: 0.25
            },
            '& .DropdownSelect-optionText .MuiTypography-root': {
              ...typography.body3,
              lineHeight: 'calc(4 / 3)'
            }
          },
          '& .DropdownSelect-indicator': {
            p: 0.5
          }
        }}
      />
      {isStatusLoading && <CircularProgress size={20} />}
    </Stack>
  );
};

export default FolderStatusSelect;
